import { useLayoutEffect, useRef, useState } from "react";
import { Box } from "@hightouch/htui";
import { wrapComponentForStandaloneRender } from "@hightouch/core/content/component-normalization/canonical-style";

import { LazyEmailPreview } from "src/components/email-preview";
import { useComponentEmailStyleConfig } from "src/pages/lifecycle-studio/emails/components/use-component-email-style-config";

type Props = {
  /** Raw component (headless fragment) HTML, or a full email document. */
  html: string | null | undefined;
  /**
   * When true, the preview scrolls internally at the container's height (for a
   * readable full-size preview, e.g. the modal's preview panel). When false
   * (default) the whole component is scaled to fit the box (grid cards).
   */
  scrollable?: boolean;
  /**
   * Wrap the HTML for standalone component render (default) — required for
   * headless component fragments. Pass false when `html` is already a full
   * email document (e.g. a template's resolved HTML).
   */
  wrapAsComponent?: boolean;
};

/**
 * Renders a component's rendered preview filling its parent box. EmailPreview
 * needs explicit pixel dimensions, so this measures the container and passes
 * them through — letting a component card show a full rendered preview in a
 * fluid grid cell (mirrors how product/image cards fill via objectFit: cover).
 * The HTML is wrapped for standalone render so the fragment sits in its
 * centered content column instead of stretching full-width.
 */
export const FillEmailPreview = ({
  html,
  scrollable = false,
  wrapAsComponent = true,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null,
  );
  const emailStyleConfig = useComponentEmailStyleConfig();

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () =>
      setSize({ width: el.offsetWidth, height: el.offsetHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const wrapped = html
    ? wrapAsComponent
      ? wrapComponentForStandaloneRender(html, emailStyleConfig)
      : html
    : null;

  return (
    <Box ref={containerRef} width="100%" height="100%" overflow="hidden">
      {size &&
        (scrollable ? (
          <LazyEmailPreview
            html={wrapped}
            width={size.width}
            height={size.height}
            scrollable
          />
        ) : (
          <LazyEmailPreview
            html={wrapped}
            width={size.width}
            height={size.height}
          />
        ))}
    </Box>
  );
};
